import { useState } from 'react';

interface Props {
    /** Pre-formed text describing what's being lifted, e.g. "last4=0412 KS" */
    targetLabel: string;
    onSubmit: (reason: string) => void | Promise<void>;
    onCancel: () => void;
}

const PRESET_REASONS = [
    'Time served / banishment period ended',
    'Mistaken identity',
    'Owner / management override',
    'Resolved with guest',
    'Other (see notes)',
];

/**
 * Reason-required modal for lifting a ban. Mirrors the BanModal
 * pattern but with green/lift styling. Confirm disabled until a
 * non-blank reason is selected. Server-side BansService.deactivate
 * also enforces non-empty reason — this is UX, not the security
 * gate.
 */
const UnbanModal = ({ targetLabel, onSubmit, onCancel }: Props) => {
    const [reason, setReason] = useState('');
    const [extra, setExtra] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const finalReason =
        reason === 'Other (see notes)'
            ? extra.trim()
            : reason
            ? extra.trim()
                ? `${reason} — ${extra.trim()}`
                : reason
            : '';
    const canSubmit = finalReason.length > 0 && !submitting;

    const submit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await onSubmit(finalReason);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            role="dialog"
            aria-modal="true"
            aria-label="Lift ban"
            style={{
                position: 'fixed',
                inset: 0,
                background: 'rgba(0,0,0,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 9000,
            }}
            onClick={(e) => {
                if (e.target === e.currentTarget) onCancel();
            }}
        >
            <div
                style={{
                    background: '#1a1a22',
                    color: '#fff',
                    border: '1px solid #39ff14',
                    borderRadius: 12,
                    padding: '24px 28px',
                    minWidth: 460,
                    maxWidth: '92vw',
                    boxShadow: '0 18px 48px rgba(0,0,0,0.7)',
                }}
            >
                <h2
                    style={{
                        marginTop: 0,
                        marginBottom: 8,
                        color: '#9be39b',
                        letterSpacing: 1,
                    }}
                >
                    ✓ Lift ban
                </h2>
                <div
                    style={{
                        marginBottom: 18,
                        fontSize: '0.9rem',
                        color: '#aaa',
                    }}
                >
                    Target: <code>{targetLabel}</code>
                </div>

                <label
                    style={{
                        display: 'block',
                        fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    Reason for lifting <span style={{ color: '#ff5555' }}>*</span>
                </label>
                <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    style={{
                        width: '100%',
                        padding: 10,
                        marginBottom: 12,
                        background: '#111',
                        color: '#fff',
                        border: '1px solid #555',
                        borderRadius: 6,
                        fontSize: 15,
                    }}
                >
                    <option value="">— select reason —</option>
                    {PRESET_REASONS.map((r) => (
                        <option key={r} value={r}>
                            {r}
                        </option>
                    ))}
                </select>

                <label
                    style={{
                        display: 'block',
                        fontWeight: 600,
                        marginBottom: 6,
                    }}
                >
                    Additional context
                    {reason === 'Other (see notes)' ? (
                        <span style={{ color: '#ff5555' }}> *</span>
                    ) : (
                        <span style={{ color: '#888' }}> (optional)</span>
                    )}
                </label>
                <textarea
                    value={extra}
                    onChange={(e) => setExtra(e.target.value)}
                    rows={3}
                    placeholder={
                        reason === 'Other (see notes)'
                            ? 'Required when "Other" selected'
                            : 'Optional detail'
                    }
                    style={{
                        width: '100%',
                        padding: 10,
                        marginBottom: 18,
                        background: '#111',
                        color: '#fff',
                        border: '1px solid #555',
                        borderRadius: 6,
                        fontFamily: 'inherit',
                        fontSize: 14,
                        boxSizing: 'border-box',
                    }}
                />

                <div
                    style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 10,
                    }}
                >
                    <button
                        type="button"
                        onClick={onCancel}
                        style={{
                            padding: '10px 18px',
                            background: '#333',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: 'pointer',
                            fontSize: 14,
                        }}
                    >
                        Cancel
                    </button>
                    <button
                        type="button"
                        onClick={submit}
                        disabled={!canSubmit}
                        style={{
                            padding: '10px 18px',
                            background: canSubmit ? '#1a6a1a' : '#1a6a1a66',
                            color: '#fff',
                            border: 'none',
                            borderRadius: 6,
                            cursor: canSubmit ? 'pointer' : 'not-allowed',
                            fontSize: 14,
                            fontWeight: 700,
                            letterSpacing: 1,
                        }}
                    >
                        {submitting ? 'Lifting…' : '✓ Confirm lift'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UnbanModal;
